import type { PayloadInterface, PayloadInterfaceLegacy } from "@ihr-radioedit/inferno-core";
import { BlockBase } from "@ihr-radioedit/inferno-core";
import type { ErrorResponse, Recipient } from "../decoders/PostUp.types";
import type { OnAirScheduleBlockResolved, ResolvedTitle } from "@ihr-radioedit/inferno-core";
import type { ResolvedBlockData } from "@ihr-radioedit/inferno-core";
import type { BlockFragment, SitesFeedPwsResult } from "@ihr-radioedit/inferno-webapi";
import * as Webapi from "@ihr-radioedit/inferno-webapi";
import type { ResolvedChartListBlock } from "@ihr-radioedit/inferno-core";

export function isChartListBlock(v?: any): v is ResolvedChartListBlock {
  return !!(v && v.hasOwnProperty("chartTitle") && v.hasOwnProperty("list") && isChartList(v.list));
}

export function isChartList(v?: any): v is Webapi.ChartsListFragment {
  return !!(
    v &&
    v.hasOwnProperty("topItems") &&
    v.hasOwnProperty("slug") &&
    v.hasOwnProperty("image") &&
    v.hasOwnProperty("title") &&
    v.hasOwnProperty("songs")
  );
}

export function isChart(v?: any): v is Webapi.ChartV2Fragment {
  return !!(
    v &&
    v.hasOwnProperty("slug") &&
    v.hasOwnProperty("id") &&
    v.hasOwnProperty("title") &&
    v.hasOwnProperty("subtitle")
  );
}

export function isAptivadaAppFragment(v?: ResolvedBlockData): v is Webapi.AptivadaAppFragment {
  return !!(
    v &&
    v.hasOwnProperty("appType") &&
    v.hasOwnProperty("appId") &&
    v.hasOwnProperty("parentAppId") &&
    v.hasOwnProperty("title") &&
    v.hasOwnProperty("shareTitle") &&
    v.hasOwnProperty("tags") &&
    v.hasOwnProperty("gridStatus") &&
    v.hasOwnProperty("gridRedirect") &&
    v.hasOwnProperty("pageUrl")
  );
}

export function isFeedResult(
  v?:
    | Webapi.FeedResult_SitesFeedLeadsResult_Fragment
    | Webapi.FeedResult_SitesFeedPubsubResult_Fragment
    | Webapi.FeedResult_SitesFeedPwsResult_Fragment
    | Pick<SitesFeedPwsResult, "id" | "type">
    | null
    | undefined,
): v is
  | Webapi.FeedResult_SitesFeedLeadsResult_Fragment
  | Webapi.FeedResult_SitesFeedPubsubResult_Fragment
  | Webapi.FeedResult_SitesFeedPwsResult_Fragment {
  return !!(v && v.hasOwnProperty("id") && v.hasOwnProperty("type") && v.hasOwnProperty("record"));
}

export function isPublishRecord(
  v?: Webapi.FeedResultFragment["record"] | undefined,
): v is Webapi.PublishRecordFragment {
  return !!(
    v &&
    v.hasOwnProperty("payload") &&
    v.hasOwnProperty("pub_start") &&
    v.hasOwnProperty("slug") &&
    v.hasOwnProperty("subscription") &&
    v.hasOwnProperty("summary")
  );
}

export function isCalendarPublishRecord(
  v?: Webapi.FeedResultFragment["record"] | undefined,
): v is Webapi.PublishRecordFragment {
  return !!(v && isPublishRecord(v) && v.hasOwnProperty("type") && v.type === "content:calendar");
}

export function isAudienceAppRecord(
  v?: Webapi.FeedResultFragment["record"] | undefined,
): v is Webapi.AudienceRecordFragment {
  return !!(v && v.hasOwnProperty("terms"));
}

function isPWSGuestRecordSummary(v: any): v is Webapi.PwsGuestRecordFragment {
  return (
    !!v.summary &&
    v.summary.hasOwnProperty("title") &&
    v.summary.hasOwnProperty("description") &&
    v.summary.hasOwnProperty("image")
  );
}

export function isPWSGuestRecord(
  v?: Webapi.FeedResultFragment["record"] | undefined,
): v is Webapi.PwsGuestRecordFragment {
  return !!(
    v &&
    v.hasOwnProperty("id") &&
    v.hasOwnProperty("slug") &&
    v.hasOwnProperty("summary") &&
    isPWSGuestRecordSummary(v)
  );
}

export function isLeadsCard(v?: Webapi.FeedResultFragment["record"] | undefined): v is Webapi.LeadsCardFragment {
  return !!(
    v &&
    v.hasOwnProperty("catalog") &&
    v.hasOwnProperty("link") &&
    v.hasOwnProperty("use_catalog_image") &&
    v.hasOwnProperty("subtitle") &&
    v.hasOwnProperty("title")
  );
}

export function isResolvedTitle(v: any): v is ResolvedTitle {
  return !!v && v.hasOwnProperty("value") && v.hasOwnProperty("topicTitle");
}

export function isItemBlock(block?: BlockFragment): block is Webapi.Block_SitesItemBlock_Fragment {
  return block && ["item-reference", "item"].includes(block.type) && (block as any).hasOwnProperty("item");
}

export function isFeedBlock(block?: BlockFragment): block is Webapi.Block_SitesFeedBlock_Fragment {
  return block && block.type === "feed" && (block as any).hasOwnProperty("feed");
}

export function isCalendarFeedBlock(block?: BlockFragment): block is Webapi.Block_SitesFeedBlock_Fragment {
  return !!(
    block &&
    isFeedBlock(block) &&
    (block?.value.feed_id === "ID:calendar-topics-feed-matsubs" ||
      isCalendarPublishRecord(block.feed?.results[0]?.record))
  );
}

export function isAudienceFeedBlock(block?: BlockFragment): block is Webapi.Block_SitesFeedBlock_Fragment {
  return !!(
    block &&
    isFeedBlock(block) &&
    (block?.value.feed_id === "ID:contest-rules-feed-audience-promobuilder" ||
      isAudienceAppRecord(block.feed?.results[0]?.record))
  );
}

// Block_SitesKeywordContestBlock_Fragment
// KeywordContestFragment
export function isKeywordContestBlock(block?: BlockFragment): block is Webapi.Block_SitesKeywordContestBlock_Fragment {
  return block && block.type === "keywordcontest" && (block as any).hasOwnProperty("schedule");
}
export interface OnAirScheduleBlock {
  resolved: OnAirScheduleBlockResolved;
}

export function isOnAirScheduleBlock(block: any): block is OnAirScheduleBlock {
  return !!(
    block &&
    block.resolved &&
    block.resolved.schedule &&
    block.resolved.schedule.names &&
    block.resolved.schedule.names.length
  );
}

export function isRecipientResponse(response: any): response is Recipient {
  return !!(response && response.recipientId && response.status);
}

export function isErroneousResponse(response: any): response is ErrorResponse {
  return response && response.data && response.status === "error";
}

export function isCurrentlyPlayingTrackDetailFragment(v: any): v is Webapi.CurrentlyPlayingTrackDetailFragment {
  return (
    v &&
    v.hasOwnProperty("dataSource") &&
    v.hasOwnProperty("startTime") &&
    v.hasOwnProperty("endTime") &&
    v.hasOwnProperty("status") &&
    v.hasOwnProperty("offset") &&
    v.hasOwnProperty("trackDurationInSec")
  );
}

export function isTopTrackDetailFragmentList(v: any[]): v is Webapi.TopTrackDetailFragment[] {
  return v.map(track => !isCurrentlyPlayingTrackDetailFragment(track)).length === v.length;
}

export const isPayloadInterface = (
  v: PayloadInterfaceLegacy | Webapi.ContentPayload | PayloadInterface,
): v is PayloadInterface => v.hasOwnProperty("fields");

export function isYouTubeBlock(block: BlockFragment): block is Webapi.Block_SitesYoutubeBlock_Fragment {
  return block && block.type === "youtube" && block.hasOwnProperty("default_video_id");
}

export function isYouTubePlaylist(block: BlockFragment): block is Webapi.Block_SitesYoutubeBlock_Fragment {
  return block && block.type === "youtube" && block.hasOwnProperty("playlist");
}

export function isYouTubeChannel(block: BlockFragment): block is Webapi.Block_SitesYoutubeBlock_Fragment {
  return block && block.type === "youtube" && block.hasOwnProperty("channel");
}

export function isBlockScheduled({ schedule }: BlockBase, now = Date.now()) {
  if (!schedule || !schedule.enabled) {
    return true;
  }

  const begin = schedule.begin || now;
  const end = schedule.end || Number.MAX_SAFE_INTEGER;

  return begin <= now && end > now;
}
